import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';

import localize from 'scaleflow/utils/localize';

function parseDataset(dataset) {
  if (!dataset || dataset.length < 2) return [];
  const [fields, ...rows] = dataset;
  const yearIdx = fields.indexOf('year');
  const periodNumberIdx = fields.indexOf('period_number');
  const valueIdx = fields.indexOf('value');
  return rows.map((row) => ({
    year: Number(row[yearIdx]),
    period_number:
      row[periodNumberIdx] != null && row[periodNumberIdx] !== ''
        ? Number(row[periodNumberIdx])
        : null,
    value: row[valueIdx] != null ? Number(row[valueIdx]) : null,
  }));
}

export default class MetricsWidgetComponent extends Component {
  @service('byod-metrics') byodMetrics;
  @service intl;

  @tracked aggregates = [];
  @tracked rawDataset = [];
  @tracked isLoading = false;
  @tracked latestValue = null;
  @tracked previousValue = null;
  @tracked isChartModalOpen = false;

  get metricModel() {
    return this.args.metric?.content ?? this.args.metric;
  }

  get level() {
    return (this.metricModel?.reporting_period ?? 'month').toLowerCase();
  }

  get diff() {
    if (this.latestValue === null || this.previousValue === null) return null;
    return this.latestValue - this.previousValue;
  }

  get direction() {
    if (this.diff === null) return null;
    if (this.diff > 0) return 'up';
    if (this.diff < 0) return 'down';
    return 'flat';
  }

  get sentiment() {
    if (this.direction === null) return null;
    const trend = this.metricModel?.trend;
    if (trend === 'NEUTRAL' || this.direction === 'flat') return 'neutral';
    if (trend === 'UP') return this.direction === 'up' ? 'good' : 'bad';
    if (trend === 'DOWN') return this.direction === 'down' ? 'good' : 'bad';
    return 'neutral';
  }

  get formattedDiff() {
    if (this.diff === null) return null;
    const symbol = this.metricModel?.symbol;
    const abs = Math.abs(this.diff);
    let formatted;
    if (symbol === 'percentage') {
      formatted = localize(abs, '%');
    } else if (symbol === 'currency') {
      formatted = localize(abs, ',-', this.intl.t('currency.euro'));
    } else {
      formatted = localize(abs);
    }
    const sign = this.diff > 0 ? '+ ' : '- ';
    return `${sign}${formatted}`;
  }

  get formattedLatestValue() {
    if (this.latestValue === null) return null;
    const symbol = this.metricModel?.symbol;
    if (symbol === 'percentage') return localize(this.latestValue, '%');
    if (symbol === 'currency')
      return localize(this.latestValue, ',-', this.intl.t('currency.euro'));
    return localize(this.latestValue);
  }

  get periodLabel() {
    const labels = {
      day: 'Daily',
      week: 'Weekly',
      month: 'Monthly',
      quarter: 'Quarterly',
      year: 'Yearly',
    };
    return labels[this.level] ?? this.level;
  }

  get hasData() {
    return this.aggregates.length > 0;
  }

  get chartTargets() {
    return this.metricModel?.targets?.content ?? [];
  }

  @action
  openChartModal() {
    this.isChartModalOpen = true;
  }

  @action
  closeChartModal() {
    this.isChartModalOpen = false;
  }

  @action
  async loadData() {
    const metric = this.metricModel;
    if (!metric?.id) return;

    this.isLoading = true;
    try {
      const now = new Date();
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(now.getMonth() - 3);
      // API expects YYYY-MM month strings for all granularities (incl. days)
      const fmt = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const from = fmt(threeMonthsAgo);
      const to = fmt(now);

      const dataset = await this.byodMetrics.fetchAggregates(
        metric.id,
        this.level,
        'L',
        from,
        to,
      );

      const rows = parseDataset(dataset);
      const sorted = rows.sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return (a.period_number ?? 0) - (b.period_number ?? 0);
      });

      this.rawDataset = dataset;
      this.aggregates = sorted;

      if (sorted.length >= 2) {
        this.latestValue = sorted[sorted.length - 1].value;
        this.previousValue = sorted[sorted.length - 2].value;
      } else if (sorted.length === 1) {
        this.latestValue = sorted[0].value;
        this.previousValue = null;
      }
    } catch {
      // Aggregate data is non-critical; fail silently
    } finally {
      this.isLoading = false;
    }
  }
}
